import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../api/examApi';
import { setAuthNotice, SESSION_ENDED_NOTICE } from '../lib/authNotice';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const bootstrap = async () => {
      const storedUser = localStorage.getItem('inspire_user');
      const storedToken = localStorage.getItem('inspire_token');

      if (storedUser && storedToken) {
        try {
          // Validate the token against the backend instead of trusting
          // localStorage blindly — an expired/garbage/deactivated-user
          // token must fail over to logged-out, not look "authenticated".
          // skipAuthRedirect: the response interceptor's hard redirect is
          // for requests fired while the app is already mounted; here we
          // handle the failure ourselves via React state.
          await authApi.getMe({ skipAuthRedirect: true });
          setUser(JSON.parse(storedUser));
        } catch (err) {
          // Only a 401 means "session dead"; a network blip is not worth a banner.
          if (err?.response?.status === 401) setAuthNotice(SESSION_ENDED_NOTICE);
          localStorage.removeItem('inspire_user');
          localStorage.removeItem('inspire_token');
          setUser(null);
        }
      }
      setLoading(false);
    };
    bootstrap();
  }, []);

  const login = (userData, token) => {
    setUser(userData);
    localStorage.setItem('inspire_user', JSON.stringify(userData));
    localStorage.setItem('inspire_token', token);
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('inspire_user', JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('inspire_user');
    localStorage.removeItem('inspire_token');
  };

  const getToken = () => {
    return localStorage.getItem('inspire_token');
  };

  const isStudent = () => user?.user_type === 'student';
  const isFaculty = () => user?.user_type === 'faculty';
  const isAdmin = () => user?.user_type === 'admin';

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      updateUser,
      logout, 
      getToken,
      isStudent,
      isFaculty,
      isAdmin,
      isAuthenticated: !!user 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
